import { TrendingUp } from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { useDealStatusChoices } from "../deals/dealStatuses";
import { ChoiceDot } from "../misc/ChoiceBadge";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";

// Enough to cover a small venue's whole pipeline in one request.
const MAX_DEALS = 1000;

/**
 * Opportunities per pipeline stage: one bar per stage, split by status
 * (open, late, won, lost) and scaled on the biggest stage amount.
 */
export const DealsByStage = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { dealStages, currency } = useConfigurationContext();
  const statuses = useDealStatusChoices();
  const { data: deals = [] } = useGetList<Deal>("deals", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: MAX_DEALS },
  });

  const stages = dealStages.map((stage) => {
    const stageDeals = deals.filter((deal) => deal.stage === stage.value);
    return {
      ...stage,
      count: stageDeals.length,
      amount: sumAmounts(stageDeals),
      segments: statuses.map((status) => ({
        ...status,
        amount: sumAmounts(
          stageDeals.filter((deal) => deal.status === status.value),
        ),
      })),
    };
  });
  const maxAmount = Math.max(1, ...stages.map((stage) => stage.amount));
  const compact = new Intl.NumberFormat(locale, {
    notation: "compact",
    style: "currency",
    currency,
    maximumFractionDigits: 1,
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <TrendingUp className="text-muted-foreground size-5 shrink-0 mr-3" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex-1">
          {translate("crm.dashboard.deals_pipeline")}
        </h2>
      </div>
      <Card className="p-4 gap-3">
        {stages.map((stage) => (
          <Link
            key={stage.value}
            to="/deals"
            className="grid grid-cols-[9rem_1fr_auto] items-center gap-3 rounded-md -mx-1 px-1 py-0.5 hover:bg-muted/60"
          >
            <span className="text-sm truncate" title={stage.label}>
              {stage.label}
            </span>
            <span className="flex h-2.5 rounded-full bg-muted overflow-hidden">
              {stage.segments.map((segment) =>
                segment.amount > 0 ? (
                  <span
                    key={segment.value}
                    title={`${segment.label} : ${compact.format(segment.amount)}`}
                    style={{
                      width: `${(segment.amount / maxAmount) * 100}%`,
                      backgroundColor: segment.color,
                    }}
                  />
                ) : null,
              )}
            </span>
            <span className="text-sm tabular-nums text-right whitespace-nowrap">
              {compact.format(stage.amount)}
              <span className="text-muted-foreground"> · {stage.count}</span>
            </span>
          </Link>
        ))}
        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 border-t text-xs text-muted-foreground">
          {statuses.map((status) => (
            <ChoiceDot key={status.value} choice={status} />
          ))}
        </div>
      </Card>
    </div>
  );
};

const sumAmounts = (deals: Deal[]) =>
  deals.reduce((sum, deal) => sum + (deal.amount ?? 0), 0);
